import {
  CompaniesRequest,
  UpdateCompanyRequest,
  GetCompanyRequest,
  UpdateCompanySettingsRequest,
  CreateCompanyRequest,
  GetCompanyTokenRequest,
  CreateCompanyTokenRequest,
  DeleteCompanyTokenRequest,
  RefreshCompanyTokenRequest,
  validateSSOSettingsRequest,
  saveSSOSettingsRequest,
  getSSOSettingsRequest,
  loadOktaGroupsRequest,
  UpdateSsoRoleSettingsRequest,
  fetchOktaRoleRequest,
  getSSOMappingRequest,
  getDefaultRoleRequest,
} from '../api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { RequestPagination } from '~/common/types';
import { Company, CompanyRequest } from '../types';
import { Settings } from '~/features/types';

export class CompanyService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async fetch(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new CompaniesRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    return this.apiClient.request(request);
  }
  async get(companyId: string, params?: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new GetCompanyRequest({
      token: accessToken,
      companyId,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async update(
    companyId: string,
    company: CompanyRequest,
    params?: { include: string },
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateCompanyRequest({
      token: accessToken,
      companyId,
      company,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async updateSettings(
    companyId: string,
    featureId: string,
    settings: Settings,
    params?: { include: string },
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateCompanySettingsRequest({
      token: accessToken,
      companyId,
      featureId,
      settings,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async create(company: Company) {
    const accessToken = this.userStore.accessToken;
    const request = new CreateCompanyRequest({
      token: accessToken,
      company,
    });

    return this.apiClient.request(request);
  }

  async fetchToken(company: string) {
    const accessToken = this.userStore.accessToken;
    const request = new GetCompanyTokenRequest({
      token: accessToken,
      company,
    });
    return this.apiClient.request(request);
  }
  async deleteToken(company: string) {
    const accessToken = this.userStore.accessToken;
    const request = new DeleteCompanyTokenRequest({
      token: accessToken,
      company,
    });
    return this.apiClient.request(request);
  }
  async refreshToken(company: string, tokenID: string) {
    const accessToken = this.userStore.accessToken;
    const request = new RefreshCompanyTokenRequest({
      token: accessToken,
      company,
      tokenID,
    });
    return this.apiClient.request(request);
  }

  async createToken(
    companyId: string,
    label: string,
    expiry?: string | null,
    entity_model = 'Company',
  ) {
    const accessToken = this.userStore.accessToken;

    const request = new CreateCompanyTokenRequest({
      token: accessToken,
      companyToken: {
        entity_id: companyId,
        label,
        expires_at: expiry,
        entity_model,
      },
    });

    return this.apiClient.request(request);
  }

  async validateSSOSettings(
    company_id: string,
    issuer: string,
    client_id: string,
    client_secret: string,
    provider: string,
    api_token: string,
    is_existing_verification: number,
  ) {
    const accessToken = this.userStore.accessToken;
    const normalizedProvider = provider?.toLowerCase?.() ?? provider;
    const request = new validateSSOSettingsRequest({
      token: accessToken,
      SsoDetail: {
        company_id,
        provider: normalizedProvider,
        issuer,
        client_id,
        client_secret,
        api_token,
        is_existing_verification,
      },
    });
    return this.apiClient.request(request);
  }
  async saveSSOSettings(
    company_id: string,
    issuer: string,
    client_id: string,
    client_secret: string,
    provider = 'okta',
    api_token: string,
  ) {
    const accessToken = this.userStore.accessToken;
    const normalizedProvider = provider?.toLowerCase?.() ?? provider;
    const request = new saveSSOSettingsRequest({
      token: accessToken,
      SsoDetail: {
        company_id,
        provider: normalizedProvider,
        issuer,
        client_id,
        client_secret,
        api_token,
      },
    });
    return this.apiClient.request(request);
  }

  async getSSOSettings(company: string, provider: string, include?: string) {
    const accessToken = this.userStore.accessToken;
    const normalizedProvider = provider?.toLowerCase?.() ?? provider;
    const request = new getSSOSettingsRequest({
      token: accessToken,
      company,
      provider: normalizedProvider,
      include,
    });
    return this.apiClient.request(request);
  }

  async loadOktaGroups(company: string) {
    const accessToken = this.userStore.accessToken;
    const request = new loadOktaGroupsRequest({
      token: accessToken,
      company,
    });
    return this.apiClient.request(request);
  }

  async saveSsoRoleSettings(
    company: string,
    default_role: string,
    payload: any,
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateSsoRoleSettingsRequest({
      token: accessToken,
      company,
      default_role,
      payload,
    });
    return this.apiClient.request(request);
  }

  async fetchOktaRole(company: string) {
    const accessToken = this.userStore.accessToken;
    const request = new fetchOktaRoleRequest({
      token: accessToken,
      company: company,
    });
    return this.apiClient.request(request);
  }
  async getSSOMapping(company: string) {
    const accessToken = this.userStore.accessToken;
    const request = new getSSOMappingRequest({
      token: accessToken,
      company: company,
    });
    return this.apiClient.request(request);
  }

  async getDefaultRole(company: string) {
    const accessToken = this.userStore.accessToken;
    const request = new getDefaultRoleRequest({
      token: accessToken,
      company: company,
    });
    return this.apiClient.request(request);
  }
}
